import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["appearance", "children", "defaultSelected", "fluid", "manualActivation", "onSelected", "selected"];
import PropTypes from 'prop-types';
import React, { forwardRef, useState } from 'react';
import { TABS_APPEARANCES } from '../../constants';
import { Tab } from '../Tab/Tab';
import { TabList } from '../TabList/TabList';
import { TabPanel } from '../TabPanel/TabPanel';
import { TabPanels } from '../TabPanels/TabPanels';
import { TabsContext } from './Tabs.context';
import { StyledTabs } from './Tabs.styles';

/**
 * A `Tabs` component acts as the container for `Tabs.List` and `Tabs.Panels`.
 *
 * If you aren't using `Tabs.Panels`, you can use `Tabs.List` without this `Tabs` parent,
 * if you prefer.
 *
 * @subcomponents ['Tabs.List', 'Tabs.Panel', 'Tabs.Panels', 'Tabs.Tab']
 * @since 8.10.0
 */
export var Tabs = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var appearance = _ref.appearance,
    children = _ref.children,
    defaultSelected = _ref.defaultSelected,
    fluid = _ref.fluid,
    manualActivation = _ref.manualActivation,
    onSelected = _ref.onSelected,
    selected = _ref.selected,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(defaultSelected),
    _useState2 = _slicedToArray(_useState, 2),
    selectedState = _useState2[0],
    setSelectedState = _useState2[1];
  return /*#__PURE__*/React.createElement(TabsContext.Provider, {
    value: {
      appearance: appearance,
      fluid: fluid,
      manualActivation: manualActivation,
      onSelected: onSelected,
      selected: selected,
      selectedState: selectedState,
      setSelectedState: setSelectedState
    }
  }, /*#__PURE__*/React.createElement(StyledTabs, _extends({
    appearance: appearance,
    fluid: fluid,
    ref: forwardedRef
  }, rest), children));
});
Tabs.displayName = 'Tabs';
Tabs.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The style of `Tabs.Tab` to display. Overrides `appearance` set on `Tabs.List`.
   */
  appearance: PropTypes.oneOf(Object.values(TABS_APPEARANCES)),
  /**
   * `Tabs` expects `Tabs.List` and `Tabs.Panels` as direct children.
   */
  children: PropTypes.node.isRequired,
  /**
   * The id of the `Tabs.Tab` to select by default. When set, `Tabs` will behave as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   *
   * `Tabs` must be set with either `selected` or `defaultSelected`, but not both.
   *
   * Overrides `defaultSelected` set on `Tabs.List`.
   */
  defaultSelected: PropTypes.string,
  /**
   * When `true`, the `Tabs.List` stretches to fill its container and tries to maintain
   * equal tab widths. Overrides `fluid` set on `Tabs.List`.
   */
  fluid: PropTypes.bool,
  /**
   * Describes the behavior when navigating the `Tabs.List`
   * with arrow keys. By default, when a new `Tabs.Tab` is given
   * focus, its corresponding `Tabs.Panel` will
   * display automatically. When `manualActivation` is set to "true", the user must press
   * the Spacebar or Enter key before the panel is displayed.
   *
   * In circumstances where content can be displayed instantly (i.e., all `Tabs.Panel` content is
   * present in the DOM), use the default behavior. If changing panels requires first loading the
   * content, set `manualActivation` to "true".
   * https://www.w3.org/TR/wai-aria-practices-1.2/#kbd_selection_follows_focus
   */
  manualActivation: PropTypes.bool,
  /**
   * Callback for when a new tab is selected.
   * Passes the id of the selected `Tabs.Tab`.
   */
  onSelected: PropTypes.func,
  /**
   * The id of the selected `Tabs.Tab`. When used, `Tabs` will
   * behave as a [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   *
   * `Tabs` must be set with either `selected` or `defaultSelected`, but not both.
   *
   * Overrides `selected` set on `Tabs.List`.
   */
  selected: PropTypes.string
} : {};
Tabs.defaultProps = {
  appearance: TABS_APPEARANCES.default
};
Tabs.List = TabList;
Tabs.Panel = TabPanel;
Tabs.Panels = TabPanels;
Tabs.Tab = Tab;